import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ResponsiveModal } from "@answersnow/pkg-theme";
import PandaDocContainer from "components/PandaDocContainer";
import useGetPandaDocSessionForDocument from "../../../hooks/api/documents/useGetPandaDocSessionForDocument";
import { getTaskDisplayName } from "../utils/getTaskDisplayName";

interface DocumentSignatureProps {
  open: boolean;
  onClose: () => void;
  refetchTasks: () => void;
  data: any;
}

const DocumentSignature = ({
  open,
  onClose,
  refetchTasks = () => {},
  data,
}: DocumentSignatureProps) => {
  const { t } = useTranslation(["dashboard"]);
  const [completed, setCompleted] = useState(false);
  const [internalOpen, setInternalOpen] = useState(true);

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const documentId = data?.metadata?.documentId ?? data?.metadata?.document_id;
  const taskTitle = getTaskDisplayName(data, t);

  const { pandaDocSession, isLoading, error } = useGetPandaDocSessionForDocument(
    documentId,
    open && !!documentId
  );

  const pandaDocSessionId = pandaDocSession?.pandaDocSessionId;
  const pandaDocDocumentId = pandaDocSession?.pandaDocDocumentId;
  const isReady = !!pandaDocSessionId && !!pandaDocDocumentId;
  const shouldShowError = open && !isLoading && (!!error || !isReady);

  const handleClose = () => {
    setInternalOpen(false);
  };

  const handleComplete = async () => {
    setTimeout(() => refetchTasks(), 3000);
    handleClose();
  };

  const handlePandaDocComplete = useCallback(() => {
    setCompleted(true);
  }, []);

  return (
    <ResponsiveModal
      open={internalOpen}
      onClose={handleClose}
      onTransitionExited={onClose}
      onOpen={() => {
        setInternalOpen(true);
      }}
      title={taskTitle}
      maxWidth="lg"
      dialogSx={{
        "& .MuiDialog-paper": {
          height: "90vh",
          maxHeight: "90vh",
          width: "90vw",
          maxWidth: "90vw",
        },
      }}
      drawerSx={{
        height: "95vh",
        maxHeight: "95vh",
      }}
      drawerContentSx={{
        height: "calc(95vh - 140px)",
        maxHeight: "calc(95vh - 140px)",
        overflowY: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: isDesktop ? "80vh" : "100%",
          minHeight: isDesktop ? "600px" : "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            pb: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="h6" sx={{ flex: 1 }}>
            {taskTitle}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            flexGrow: 1,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
            position: "relative",
            minHeight: isDesktop ? "auto" : "300px",
            overflow: isDesktop ? "hidden" : "auto",
          }}
        >
          {isLoading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "background.default",
                zIndex: 1,
              }}
            >
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2 }}>
                {t("dashboard:parent.myTasks.documentSignature.loadingDocument")}
              </Typography>
            </Box>
          )}

          {shouldShowError && (
            <Box sx={{ p: 3 }}>
              <Alert severity="error">
                {t("dashboard:parent.myTasks.documentSignature.sessionNotAvailable")}
              </Alert>
            </Box>
          )}

          {isReady && (
            <Box sx={{ height: "100%", minHeight: "60vh", padding: 2 }}>
              <PandaDocContainer
                pandaDocSessionId={pandaDocSessionId}
                onDocumentCompleted={handlePandaDocComplete}
                pandaDocId={pandaDocDocumentId}
              />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            mt: 2,
            pt: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Button onClick={handleClose} variant="outlined" size="medium" sx={{ minWidth: "120px" }}>
            {t("dashboard:parent.myTasks.common.closeButton")}
          </Button>
          <Button
            onClick={handleComplete}
            variant="contained"
            color="primary"
            size="medium"
            sx={{ minWidth: "120px" }}
            disabled={!completed}
          >
            {t("dashboard:parent.myTasks.documentSignature.doneButton")}
          </Button>
        </Box>
      </Box>
    </ResponsiveModal>
  );
};

export default DocumentSignature;
